import React from 'react';
import { Box, Typography } from '@mui/material';
import { Globe } from 'lucide-react';
import { format } from 'date-fns';

// Import device logos
import appleLogo from '../assets/apple_logo.png';
import androidLogo from '../assets/android_logo.png';
import windowsLogo from '../assets/windows_logo.png';

/**
 * TableView component for displaying page views in a tabular format
 */
const PageViewsTable = ({ pageViews = [] }) => {
    if (!pageViews || pageViews.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: '#9e9e9e' }}>
                    No page views recorded
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            display: 'table',
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.75rem'
        }}>
            {/* Table Header */}
            <Box sx={{ display: 'table-header-group' }}>
                <Box sx={{ display: 'table-row', borderBottom: '1px solid #444' }}>
                    <Box sx={{
                        display: 'table-cell',
                        p: 1,
                        color: '#9e9e9e',
                        fontWeight: 'bold',
                        width: '60px',
                        textAlign: 'center'
                    }}>
                        Device
                    </Box>
                    <Box sx={{
                        display: 'table-cell',
                        p: 1,
                        color: '#9e9e9e',
                        fontWeight: 'bold'
                    }}>
                        Page
                    </Box>
                    <Box sx={{
                        display: 'table-cell',
                        p: 1,
                        color: '#9e9e9e',
                        fontWeight: 'bold',
                        width: '150px'
                    }}>
                        IP Address
                    </Box>
                    <Box sx={{
                        display: 'table-cell',
                        p: 1,
                        color: '#9e9e9e',
                        fontWeight: 'bold',
                        width: '200px',
                        textAlign: 'right'
                    }}>
                        Timestamp
                    </Box>
                </Box>
            </Box>

            {/* Table Body */}
            <Box sx={{ display: 'table-row-group' }}>
                {pageViews.slice(0, 10).map((view, index) => {
                    // Determine device type and logo
                    let deviceLogo;
                    let deviceIcon;
                    const device = (view.browser || '').toLowerCase();

                    if (device.includes('iphone') || device.includes('ipad') || device.includes('ios') || device.includes('mac os')) {
                        deviceLogo = appleLogo;
                    } else if (device.includes('android')) {
                        deviceLogo = androidLogo;
                    } else if (device.includes('windows')) {
                        deviceLogo = windowsLogo;
                    } else {
                        deviceIcon = <Globe size={18} color="#ba68c8" />;
                    }

                    return (
                        <Box
                            key={view.id || index}
                            sx={{
                                display: 'table-row',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                                },
                                borderBottom: index < pageViews.length - 1 ? '1px solid #333' : 'none'
                            }}
                        >
                            {/* Device column */}
                            <Box sx={{
                                display: 'table-cell',
                                p: 1,
                                verticalAlign: 'middle',
                                textAlign: 'center'
                            }}>
                                {deviceLogo ? (
                                    <img
                                        src={deviceLogo}
                                        alt="Device"
                                        style={{
                                            width: 20,
                                            height: 20,
                                            objectFit: 'contain'
                                        }}
                                    />
                                ) : deviceIcon}
                            </Box>

                            {/* Page column */}
                            <Box sx={{
                                display: 'table-cell',
                                p: 1,
                                verticalAlign: 'middle',
                                color: '#9e9e9e',
                                fontStyle: 'italic'
                            }}>
                                https://service.autofront.com.au/p/eclipse_tint
                            </Box>

                            {/* IP Address column */}
                            <Box sx={{
                                display: 'table-cell',
                                p: 1,
                                verticalAlign: 'middle',
                                color: '#e0e0e0',
                                fontWeight: 'bold'
                            }}>
                                {view.ip_address || 'Unknown'}
                            </Box>

                            {/* Timestamp column */}
                            <Box sx={{
                                display: 'table-cell',
                                p: 1,
                                verticalAlign: 'middle',
                                color: '#9e9e9e',
                                textAlign: 'right'
                            }}>
                                {format(new Date(view.created_at), 'yyyy/MM/dd, hh:mm:ss a')}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default PageViewsTable;
